import React, { useState } from 'react';
import BottomSheet from '../../DesignSystem/BottomSheet';
import { ReactComponent as CrossIcon } from '../../Icons/Cross.svg';
import s from './PartialCashlessBanner.module.scss';
import { clsx } from '../../utils';
import bannerImage from '../../Icons/partial-cashless-banner-icon.svg';

/**
 * PartialCashlessBanner
 *
 * Renders the partial-cashless information banner on the Select Lab Partners
 * page. Shows heading, subheading, a "Tests in Cart" section with up to 3 test
 * names and a "+N more" trigger that opens a bottom sheet with all tests.
 *
 * Theming is driven by `backgroundStyle`:
 *   - "success" → green tones
 *   - "warning" → yellow/amber tones
 */
const VISIBLE_TEST_COUNT = 3;

const PartialCashlessBanner = ({ data }) => {
  const [showAllTests, setShowAllTests] = useState(false);

  if (!data) return null;

  const { heading, subHeading, backgroundStyle, totalCount } = data;

  // Default to [] when the API sends undefined / null / a non-array, so
  // .slice/.length/.map below are always safe.
  const testsInCart = Array.isArray(data.testsInCart) ? data.testsInCart : [];

  const isSuccess = backgroundStyle === 'success';
  const themeClass = isSuccess ? s.success : s.warning;

  const visibleTests = testsInCart.slice(0, VISIBLE_TEST_COUNT);
  const remainingCount = testsInCart.length - VISIBLE_TEST_COUNT;

  // Names of the visible tests, dropping any items without a name.
  const visibleTestNames = visibleTests
    .map((t) => t?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <div className={clsx(s.container, themeClass)}>
        {/* Top section: heading + illustration */}
        <div className={s.topSection}>
          <div className={s.textContent}>
            <p className={s.heading}>{heading}</p>
            <p className={s.subHeading}>{subHeading}</p>
          </div>
          <img className={s.illustration} src={bannerImage} alt="" />
        </div>

        {/* Tests in Cart section */}
        {testsInCart.length > 0 && (
          <div className={clsx(s.testsBox, themeClass)}>
            <p className={s.testsLabel}>Tests in Cart</p>
            <p className={s.testsList}>
              {visibleTestNames}
              {remainingCount > 0 && (
                <>
                  ,{' '}
                  <span
                    className={s.moreLink}
                    onClick={() => setShowAllTests(true)}
                  >
                    +{remainingCount} more
                  </span>
                </>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Bottom sheet for all tests */}
      <BottomSheet
        isOpen={showAllTests}
        noPadding
        onClose={() => setShowAllTests(false)}
      >
        <div>
          {/* Header */}
          <div className={s.sheetHeader}>
            <p className={s.sheetTitle}>
              {totalCount || testsInCart.length} Tests Included
            </p>
            <button
              onClick={() => setShowAllTests(false)}
              className={s.closeButton}
            >
              <CrossIcon height={8} width={8} />
            </button>
          </div>

          {/* Divider */}
          <div className={s.divider} />

          {/* Scrollable Content */}
          <div className={s.testsScroll}>
            {testsInCart.map((test, idx) => (
              <div key={test?.id ?? test?.name ?? idx} className={s.testRow}>
                <p className={s.testRowName}>{test?.name}</p>
              </div>
            ))}
          </div>
        </div>
      </BottomSheet>
    </>
  );
};

export default PartialCashlessBanner;
